// Động Phủ cấp 1 (DONG_PHU §0–3, CRAFTING §1–2): Dược Viên (Linh Thực idle) + Đan Phòng (Luyện Đan).
// Chạy theo mốc quyết toán của vòng idle (onSettle): công trình xong, mẻ đan xong tự vào túi.
// Phần vắng vượt trần 72h không tính cho cây và lò: dời mốc tới trước đúng phần bị cắt.
import { signal, computed, batch } from '@preact/signals';
import { BALANCE } from '@data/balance';
import {
  BUILDINGS,
  HERBS,
  RECIPES,
  herbBySeed,
  herbDef,
  isRecipeId,
  recipeDef,
  type BuildingId,
  type HeatStep,
} from '@data/crafting';
import {
  goodsDef,
  herbAgeOf,
  isStackKey,
  stackKey,
  HERB_AGES,
  parseStackKey,
  type HerbAge,
} from '@data/items/goods';
import { createRng, deriveSeed } from '@sim/core/rng';
import { stepInfo } from '@sim/cultivation';
import {
  addAlchemyXp,
  buildingCap,
  defaultBuildings,
  furnaceCount,
  gardenPlots,
  growthMult,
  harvestPlot,
  herbQuality,
  jobDurationMs,
  nextJobStart,
  pickIngredientsFrom,
  resolveCraft,
  settleBuildings,
  upgradeCost,
  type AlchemyState,
  type Buildings,
  type CraftJob,
  type PlotState,
} from '@sim/crafting';
import { combatStats, type Loadout } from '@sim/items/stats';
import { playerBase } from '@sim/world/ctx';
import { isPlainObject } from '@save/types';
import { registerSlice } from '../registry';
import { now } from '../clock';
import { flush, markDirty, saveSeed } from '../persistence';
import { logLine } from './log';
import { canAfford, spend } from './wallet';
import { cultivation, onSettle } from './cultivation';
import { addGoods, goodsCount, inventory, takeGoods } from './inventory';

export interface CaveState {
  buildings: Buildings;
  plots: PlotState[];
  alchemy: AlchemyState;
  jobs: CraftJob[];
  jobSeq: number;
}

export function defaultCave(): CaveState {
  const b = defaultBuildings();
  return {
    buildings: b,
    plots: Array.from({ length: gardenPlots(b) }, () => ({ herbId: null, plantedAtMs: 0, bonusYears: 0 })),
    alchemy: { lv: 1, xp: 0, knownRecipes: [] },
    jobs: [],
    jobSeq: 0,
  };
}

const _cave = signal<CaveState>(defaultCave());
export const cave = computed(() => _cave.value);
export const caveGrowth = computed(() => growthMult(_cave.value.buildings));

function realmIdx(): number {
  return stepInfo(cultivation.peek().step).realmIdx;
}

function fitPlots(c: CaveState): PlotState[] {
  const n = gardenPlots(c.buildings);
  const out = c.plots.slice(0, n);
  while (out.length < n) out.push({ herbId: null, plantedAtMs: 0, bonusYears: 0 });
  return out;
}

/** Quyết toán Động Phủ tới `nowMs` (gọi từ vòng idle). */
function settleCave(nowMs: number, cappedMs: number): void {
  let c = _cave.peek();
  if (cappedMs > 0)
    c = {
      ...c,
      plots: c.plots.map((p) => (p.herbId ? { ...p, plantedAtMs: p.plantedAtMs + cappedMs } : p)),
      jobs: c.jobs.map((j) => ({ ...j, startMs: j.startMs + cappedMs, endMs: j.endMs + cappedMs })),
      buildings: Object.fromEntries(
        Object.entries(c.buildings).map(([k, v]) => [
          k,
          v.upgrade ? { ...v, upgrade: { ...v.upgrade, endMs: v.upgrade.endMs + cappedMs } } : v,
        ]),
      ) as Buildings,
    };
  const sb = settleBuildings(c.buildings, nowMs);
  const done = c.jobs.filter((j) => j.endMs <= nowMs);
  if (!sb.finished.length && !done.length && cappedMs === 0) return;
  batch(() => {
    let alchemy = c.alchemy;
    for (const j of done) {
      const r = resolveCraft(j, {
        alchemyLv: alchemy.lv,
        pillRoomLv: sb.buildings.pillRoom.lv,
        fire: 'mortal',
      });
      addGoods({ [r.output.stack]: r.output.count });
      alchemy = addAlchemyXp(alchemy, r.xp, realmIdx());
      logLine(
        'cultivation',
        r.grade === 'waste' ? 'log.alchemy.waste' : 'log.alchemy.done',
        {
          item: `@${recipeDef(j.recipeId).outputId}.name`,
          grade: `@craft.pillGrade.${r.grade}`,
          n: r.output.count,
        },
        j.endMs,
      );
    }
    for (const id of sb.finished)
      logLine('cultivation', 'log.cave.built', { b: `@craft.building.${id}`, n: sb.buildings[id].lv });
    const next = { ...c, buildings: sb.buildings, alchemy, jobs: c.jobs.filter((j) => j.endMs > nowMs) };
    _cave.value = { ...next, plots: fitPlots(next) };
  });
  markDirty();
}

onSettle((nowMs, cappedMs) => {
  settleCave(nowMs, cappedMs);
  grantRecipesByStep();
});

/** Đan phương trao theo mốc cảnh giới (Trúc Cơ Đan: chạm LK9). */
function grantRecipesByStep(): void {
  const step = cultivation.peek().step;
  const c = _cave.peek();
  const add = RECIPES.filter(
    (r) => r.grantAtStep !== undefined && step >= r.grantAtStep && !c.alchemy.knownRecipes.includes(r.id),
  );
  if (!add.length) return;
  _cave.value = {
    ...c,
    alchemy: { ...c.alchemy, knownRecipes: [...c.alchemy.knownRecipes, ...add.map((r) => r.id)] },
  };
  for (const r of add) logLine('cultivation', 'log.alchemy.recipe', { item: `@${r.outputId}.name` });
  markDirty();
}

/** Hook dev / e2e: biết ngay một đan phương (bỏ qua mốc cảnh giới). */
export function devLearnRecipe(id: string): void {
  const c = _cave.peek();
  if (!isRecipeId(id) || c.alchemy.knownRecipes.includes(id)) return;
  _cave.value = { ...c, alchemy: { ...c.alchemy, knownRecipes: [...c.alchemy.knownRecipes, id] } };
}

// ── Dược Viên
export function plant(index: number, seedId: string): boolean {
  const c = _cave.peek();
  const p = c.plots[index];
  const herb = herbBySeed(seedId);
  if (!p || p.herbId || !herb || goodsCount(seedId) < 1) return false;
  const t = now();
  batch(() => {
    takeGoods({ [seedId]: 1 });
    _cave.value = {
      ...c,
      plots: c.plots.map((x, i) => (i === index ? { herbId: herb.id, plantedAtMs: t, bonusYears: 0 } : x)),
    };
  });
  markDirty();
  return true;
}

export function harvest(index: number): boolean {
  const c = _cave.peek();
  const p = c.plots[index];
  if (!p) return false;
  const t = now();
  const u = combatStats(playerBase(cultivation.peek()), inventory.peek().equipped as Loadout).utility;
  const r = harvestPlot(
    p,
    t,
    growthMult(c.buildings),
    u.herbYield,
    createRng(deriveSeed(saveSeed(), 'harvest', index, p.plantedAtMs)),
  );
  if (!r) return false;
  const age = herbAgeOf(r.years);
  batch(() => {
    const seed = herbDef(r.herbId).seedId;
    addGoods({ [stackKey(r.herbId, { age })]: r.count, ...(r.seedBack ? { [seed]: 1 } : {}) });
    _cave.value = {
      ...c,
      plots: c.plots.map((x, i) => (i === index ? { herbId: null, plantedAtMs: 0, bonusYears: 0 } : x)),
    };
    logLine(
      'cultivation',
      'log.herb.harvest',
      { item: `@${r.herbId}.name`, n: r.count, age: `@craft.herbAge.${age}` },
      t,
    );
  });
  markDirty();
  return true;
}

export function harvestAll(): number {
  let n = 0;
  _cave.peek().plots.forEach((_, i) => {
    if (harvest(i)) n++;
  });
  return n;
}

// ── Công trình
export type UpgradeBlock = 'cap' | 'busy' | 'stones' | 'material';
export function upgradeBlock(id: BuildingId): UpgradeBlock | null {
  const c = _cave.peek();
  const b = c.buildings[id];
  if (b.lv >= buildingCap(id, realmIdx())) return 'cap';
  // P0–P1: một công trình xây cùng lúc (DONG_PHU §0).
  if (Object.values(c.buildings).some((x) => x.upgrade)) return 'busy';
  const cost = upgradeCost(id, b.lv + 1);
  if (!canAfford({ spiritStone: cost.stones })) return 'stones';
  if (goodsCount(cost.material.id) < cost.material.count) return 'material';
  return null;
}

export function startUpgrade(id: BuildingId): boolean {
  if (upgradeBlock(id) !== null) return false;
  const c = _cave.peek();
  const b = c.buildings[id];
  const cost = upgradeCost(id, b.lv + 1);
  const t = now();
  batch(() => {
    spend({ spiritStone: cost.stones });
    takeGoods({ [cost.material.id]: cost.material.count });
    _cave.value = {
      ...c,
      buildings: {
        ...c.buildings,
        [id]: { ...b, upgrade: { toLv: b.lv + 1, endMs: t + cost.minutes * 60_000 } },
      },
    };
    logLine('cultivation', 'log.cave.upgrade', { b: `@craft.building.${id}`, n: b.lv + 1 }, t);
  });
  void flush();
  return true;
}

// ── Luyện Đan
/**
 * Chọn nguyên liệu trong túi cho một mẻ: dược lấy niên phân cao trước, thiếu thì bù niên phân thấp hơn
 * (trộn được; herbQ lấy trung bình theo từng cây, DECISIONS M2).
 */
export function pickIngredients(
  recipeId: string,
): { keys: Record<string, number>; ages: Record<string, HerbAge[]> } | null {
  return pickIngredientsFrom(recipeDef(recipeId), inventory.peek().goods);
}

/** Số có trong túi của một nguyên liệu (dược: cộng mọi niên phân). */
export function ingredientCount(id: string): number {
  const goods = inventory.peek().goods;
  if (goodsDef(id).kind !== 'herb') return goods[id] ?? 0;
  return HERB_AGES.reduce((a, age) => a + (goods[stackKey(id, { age })] ?? 0), 0);
}

export type CraftBlock = 'unknownRecipe' | 'ingredients' | 'furnaceBusy';
export function craftBlock(recipeId: string): CraftBlock | null {
  const c = _cave.peek();
  if (!c.alchemy.knownRecipes.includes(recipeId)) return 'unknownRecipe';
  if (c.jobs.length >= furnaceCount(c.buildings) * BALANCE.FURNACE_QUEUE_MAX) return 'furnaceBusy';
  if (!pickIngredients(recipeId)) return 'ingredients';
  return null;
}

/**
 * Bắt đầu một mẻ (nguyên liệu trừ khi bấm; mẻ vào lò rảnh sớm nhất, đủ lò thì chờ). Hỏa hầu: mẫu đã
 * biết.
 */
export function startCraft(recipeId: string): boolean {
  if (!isRecipeId(recipeId) || craftBlock(recipeId) !== null) return false;
  const pick = pickIngredients(recipeId)!;
  const r = recipeDef(recipeId);
  const c = _cave.peek();
  const t = now();
  const start = nextJobStart(c.jobs, furnaceCount(c.buildings), t);
  const seq = c.jobSeq + 1;
  const job: CraftJob = {
    id: `j${seq}`,
    recipeId,
    startMs: start,
    endMs: start + jobDurationMs(recipeId),
    rngSeed: deriveSeed(saveSeed(), 'craft', seq),
    herbQ: herbQuality(r, pick.ages),
    pattern: [...r.pattern] as [HeatStep, HeatStep, HeatStep],
    used: { ...pick.keys },
  };
  batch(() => {
    takeGoods(pick.keys);
    _cave.value = { ...c, jobs: [...c.jobs, job], jobSeq: seq };
    logLine('cultivation', 'log.alchemy.start', { item: `@${r.outputId}.name` }, t);
  });
  void flush();
  return true;
}

/** Nguyên liệu hoàn khi hủy: đúng các chồng đã trừ; mẻ cũ chưa ghi thì theo id gốc của đan phương. */
export function refundOf(j: CraftJob): Record<string, number> {
  if (j.used) return { ...j.used };
  const back: Record<string, number> = {};
  for (const ing of recipeDef(j.recipeId).ingredients) back[ing.id] = (back[ing.id] ?? 0) + ing.count;
  return back;
}

/** Sau khi hủy: mẻ chưa bắt đầu dồn lên lò rảnh sớm nhất (giữ thứ tự, giữ thời lượng). */
function reschedule(jobs: readonly CraftJob[], b: Buildings, t: number): CraftJob[] {
  const fixed = jobs.filter((j) => j.startMs <= t);
  const queued = jobs.filter((j) => j.startMs > t).sort((a, z) => a.startMs - z.startMs);
  const out = [...fixed];
  for (const j of queued) {
    const start = nextJobStart(out, furnaceCount(b), t);
    out.push({ ...j, startMs: start, endMs: start + (j.endMs - j.startMs) });
  }
  return out;
}

export type CancelBlock = 'started' | 'unknown';
export function cancelBlock(jobId: string): CancelBlock | null {
  const j = _cave.peek().jobs.find((x) => x.id === jobId);
  if (!j) return 'unknown';
  return j.startMs <= now() ? 'started' : null;
}

/** Hủy mẻ đang chờ (chưa bắt đầu): hoàn 100% nguyên liệu, đúng niên phân đã trừ. */
export function cancelCraft(jobId: string): boolean {
  if (cancelBlock(jobId) !== null) return false;
  const c = _cave.peek();
  const j = c.jobs.find((x) => x.id === jobId)!;
  batch(() => {
    addGoods(refundOf(j));
    _cave.value = {
      ...c,
      jobs: reschedule(
        c.jobs.filter((x) => x.id !== jobId),
        c.buildings,
        now(),
      ),
    };
    logLine('cultivation', 'log.alchemy.cancel', { item: `@${recipeDef(j.recipeId).outputId}.name` });
  });
  void flush();
  return true;
}

// ── Save (core.cave, từ v4)
function num(v: unknown, d: number, min = -Infinity, max = Infinity): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : d;
}

/** `used` của mẻ (tùy chọn từ bản sửa M2): chỉ giữ khóa chồng hợp lệ, số nguyên dương; rỗng thì bỏ. */
function sanitizeUsed(v: unknown): { used?: Record<string, number> } {
  if (!isPlainObject(v)) return {};
  const used: Record<string, number> = {};
  for (const [k, n] of Object.entries(v))
    if (isStackKey(k) && num(n, 0, 1) > 0) used[k] = Math.floor(n as number);
  return Object.keys(used).length ? { used } : {};
}

export function sanitizeCave(d: unknown): CaveState {
  const def = defaultCave();
  if (!isPlainObject(d)) return def;
  const buildings = { ...def.buildings };
  const braw = isPlainObject(d.buildings) ? d.buildings : {};
  for (const b of BUILDINGS) {
    const x = braw[b.id];
    if (!isPlainObject(x)) continue;
    const up = isPlainObject(x.upgrade) ? x.upgrade : null;
    const lv = Math.floor(num(x.lv, 1, 1, 10));
    buildings[b.id] = {
      lv,
      upgrade:
        up && num(up.endMs, NaN) === up.endMs
          ? { toLv: Math.min(10, lv + 1), endMs: up.endMs as number }
          : null,
    };
  }
  const plots = Array.isArray(d.plots)
    ? d.plots.map((p) =>
        isPlainObject(p) && typeof p.herbId === 'string' && HERBS.some((h) => h.id === p.herbId)
          ? { herbId: p.herbId, plantedAtMs: num(p.plantedAtMs, 0), bonusYears: num(p.bonusYears, 0, 0) }
          : { herbId: null, plantedAtMs: 0, bonusYears: 0 },
      )
    : def.plots;
  const al = isPlainObject(d.alchemy) ? d.alchemy : {};
  const jobs = Array.isArray(d.jobs)
    ? d.jobs.flatMap((j) =>
        isPlainObject(j) && typeof j.id === 'string' && isRecipeId(j.recipeId)
          ? [
              {
                id: j.id,
                recipeId: j.recipeId,
                startMs: num(j.startMs, 0),
                endMs: num(j.endMs, 0),
                rngSeed: Math.floor(num(j.rngSeed, 1, 0, 0xffffffff)),
                herbQ: num(j.herbQ, 0, 0, 30),
                pattern: [...recipeDef(j.recipeId).pattern] as [HeatStep, HeatStep, HeatStep],
                ...sanitizeUsed(j.used),
              },
            ]
          : [],
      )
    : [];
  // Seed mẻ = hash(seed, 'craft', seq): jobSeq hỏng / thiếu thì lấy lớn nhất của số save ghi và số trong
  // id mẻ còn giữ (`j<seq>`), không lùi về số đã dùng.
  const idSeq = jobs.reduce((m, j) => Math.max(m, Number(/^j(\d+)$/.exec(j.id)?.[1] ?? 0)), 0);
  const st: CaveState = {
    buildings,
    plots,
    alchemy: {
      lv: Math.floor(num(al.lv, 1, 1, 18)),
      xp: num(al.xp, 0, 0),
      knownRecipes: Array.isArray(al.knownRecipes) ? al.knownRecipes.filter(isRecipeId) : [],
    },
    jobs,
    jobSeq: Math.max(Math.floor(num(d.jobSeq, 0, 0)), idSeq),
  };
  return { ...st, plots: fitPlots(st) };
}

registerSlice<CaveState>({
  key: 'cave',
  scope: 'core',
  serialize: () => _cave.peek(),
  hydrate: (d) => {
    _cave.value = sanitizeCave(d);
  },
});

export { parseStackKey };
